import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe, DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { WorkspaceService } from '../../services/workspace.service';
import {
  BusinessCategoryGroup,
  GenerateLeadsResult,
  IcpLeadSettings,
  LeadPoolEstimate,
  LeadRunRow,
  WorkspaceLeadRow,
  WorkspaceLeadsService,
} from '../../services/workspace-leads.service';
import { IcpService } from '../../services/icp.service';
import { EchoService } from '../../services/echo.service';
import { AuthService } from '../../services/auth.service';
import { PopupService } from '../../services/popup.service';
import { Workspace } from '../../models/workspace.model';
import { ICP } from '../../state/icp/icp.state';
import { UnlockEmailConfirmModalComponent } from '../lead-pipelines/components/unlock-email-confirm-modal/unlock-email-confirm-modal.component';
import {
  DEFAULT_MANUAL_PRICES,
  LeadExport,
  LeadScope,
  MAX_EXPORT_LEADS,
  ProspectingListConfig,
  ProspectingListSummary,
  ProspectingPreview,
  ProspectingService,
  SaveListResult,
  apiErrorMessage,
  isLeadExportActive,
  missingRequiredLabel,
  saveBlob,
} from '../../services/prospecting.service';
import { selectCredits } from '../../state/subscription/subscription.selectors';
import { LeadsExportModalComponent } from './components/leads-export-modal/leads-export-modal.component';
import { ExportColumnsPickerComponent } from './components/export-columns-picker/export-columns-picker.component';
import { ContinueExportDialogComponent } from './components/continue-export-dialog/continue-export-dialog.component';
import { LeadExportColumn, loadExportColumns, saveExportColumns } from '../../utils/lead-export-columns';
import { ListExportDialogComponent } from './components/list-export-dialog/list-export-dialog.component';
import { SaveListIcpDialogComponent } from './components/save-list-icp-dialog/save-list-icp-dialog.component';

/** How often to re-check an Excel export of the prospecting list. */
const EXPORT_POLL_MS = 3000;

/** How often to re-check the run while a generation is in flight. */
const GENERATION_POLL_MS = 4000;

/** Where the page remembers the last selected ICP. */
const STORAGE_KEY = 'salesommo_leads_icp';

/** Where the page remembers the grid/list view choice. */
const VIEW_STORAGE_KEY = 'salesommo_leads_view';

/** Where the page remembers whether the generation-config card is hidden. */
const CONFIG_HIDDEN_STORAGE_KEY = 'salesommo_leads_config_hidden';

/**
 * Per-ICP manual generation config (count + applied Advanced filters), kept
 * in local storage so generating leads — or leaving and coming back — never
 * resets what the user set up. Shape: { [icpId]: {count, categories, products} }.
 */
const MANUAL_CONFIG_STORAGE_KEY = 'salesommo_leads_manual_config';

/** Sentinel value for the "+ Create new ICP" dropdown entry. */
const CREATE_ICP_OPTION = -1;

/**
 * Max height of the collapsed filter chips container (2 chip rows + gap).
 * Keep in sync with `.filters-chips-collapsed` in the SCSS.
 */
const FILTERS_COLLAPSED_MAX_PX = 52;

type LeadsTab = 'active' | 'dismissed' | 'activity';

interface PageMeta {
  page: number;
  lastPage: number;
  total: number;
}

/**
 * Simplified Leads page (v3): pick an ICP — the lead-generation context —
 * and generate 5-10 more leads deduped against that ICP's history, with
 * optional per-ICP daily automation. The workspace is only chosen in a
 * dialog when generating a report for a lead.
 */
@Component({
  selector: 'app-leads',
  standalone: true,
  imports: [NgIf, NgForOf, NgClass, FormsModule, RouterLink, DatePipe, DecimalPipe, UnlockEmailConfirmModalComponent, LeadsExportModalComponent, ListExportDialogComponent, SaveListIcpDialogComponent, ExportColumnsPickerComponent, ContinueExportDialogComponent],
  templateUrl: './leads.component.html',
  styleUrl: './leads.component.scss'
})
export class LeadsComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly CREATE_ICP_OPTION = CREATE_ICP_OPTION;

  @ViewChild('filtersChips') filtersChipsRef?: ElementRef<HTMLElement>;

  icps: ICP[] = [];
  selectedIcpId: number | null = null;

  /** Workspaces are only needed for the report-destination picker dialog. */
  workspaces: Workspace[] = [];

  leads: WorkspaceLeadRow[] = [];
  settings: IcpLeadSettings | null = null;

  /** Which list is shown. */
  activeTab: LeadsTab = 'active';
  dismissedLeads: WorkspaceLeadRow[] = [];
  isLoadingDismissed = false;
  dismissedLoadedFor: number | null = null;

  /** Activity tab: generation runs (manual + scheduled). */
  runs: LeadRunRow[] = [];
  isLoadingRuns = false;
  runsLoadedFor: number | null = null;
  runsMeta: PageMeta = { page: 1, lastPage: 1, total: 0 };

  /** Leads layout: card grid or compact list rows. Remembered per browser. */
  viewMode: 'grid' | 'list' = 'grid';

  /** Generation-config card collapsed (contents hidden). Remembered per browser. */
  configCollapsed = false;

  // ─── Advanced filters (Company Product + Business Category) ────────
  /** Dialog open state. */
  advancedOpen = false;
  /** Veridion business categories grouped by industry (for the selected ICP). */
  advCategoryGroups: BusinessCategoryGroup[] = [];
  advCategoriesLoadedFor: number | null = null;
  isLoadingAdvCategories = false;
  /** False = endpoint fell back to the FULL taxonomy (ICP industries unmatched). */
  advCategoriesFiltered = true;
  /** Working selection inside the dialog. */
  advSelectedCategories = new Set<string>();
  advProducts: string[] = [];
  advProductInput = '';
  /**
   * Filters APPLIED to manual generation and persisted by "Save automation".
   * Empty arrays mean no refinement (industry-only search, as before).
   */
  appliedBusinessCategories: string[] = [];
  appliedCompanyProducts: string[] = [];
  /** Whether the applied-filter chips list is fully expanded past 2 rows. */
  filtersExpanded = false;
  /** True when the chips list needs more than 2 rows (Show more/less visible). */
  filtersOverflowing = false;
  private filtersOverflowCheckScheduled = false;

  /** Pagination (shared per-page size, per-tab page counters). */
  perPageOptions = [10, 25, 50, 100];
  perPage = 10;
  activeMeta: PageMeta = { page: 1, lastPage: 1, total: 0 };
  dismissedMeta: PageMeta = { page: 1, lastPage: 1, total: 0 };

  restoringLeads = new Set<number>();
  deletingLeads = new Set<number>();
  /** Lead pending permanent deletion (confirm dialog open). */
  deleteConfirmLead: WorkspaceLeadRow | null = null;

  isLoadingIcps = true;
  isLoadingLeads = false;

  /** In-flight generation run (id + how many were requested), null when idle. */
  activeRun: { id: number; requested: number; found: number } | null = null;

  /** Count picked in the generate control. */
  generateCount = 5;

  /** Automation settings state (decoupled from saved settings until Save). */
  automationEnabled = false;
  automationCount = 5;
  automationTime = '09:00';
  automationTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  isSavingAutomation = false;

  /** "Available companies / persons in pool" estimate shown in the config panel. */
  poolEstimate: LeadPoolEstimate | null = null;
  isLoadingPool = false;

  /** Common timezone suggestions for the automation dialog datalist. */
  timezones: string[] = [
    'UTC',
    'Europe/Bucharest', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
  ];

  /** Per-lead in-flight action guards. */
  unlockingLeads = new Set<number>();
  enhancingLeads = new Set<number>();
  dismissingLeads = new Set<number>();
  revealingLeads = new Set<number>();

  /** Rows whose "Why this lead?" section is expanded. */
  expandedLeads = new Set<number>();

  /** Report-destination dialog state ("Generate report" flow). */
  reportPickerLead: WorkspaceLeadRow | null = null;
  reportWorkspaceId: number | null = null;
  /** True when the picker was opened from the "Regenerate" button of an already-enhanced lead. */
  reportPickerRegenerate = false;

  /** Reveal-company-leads dialog state: pick how many hidden leads to reveal. */
  revealPickerLead: WorkspaceLeadRow | null = null;
  revealCount = 1;

  /** Lead awaiting confirmation in the unlock-email cost dialog. */
  unlockTarget: WorkspaceLeadRow | null = null;

  /** CSV export in flight. */
  isExporting = false;

  // ─── Prospecting list mode (opened with ?list={listId}) ─────────────
  /** Set only while the page shows an unsaved prospecting list. */
  listId: string | null = null;
  listConfig: ProspectingListConfig | null = null;
  listDetail: ProspectingListSummary | null = null;
  /** Config not in this tab's storage yet — fetching it from the server. */
  isResolvingList = false;
  listPreview: ProspectingPreview | null = null;
  /** 5 / 10 = generate here; 'more' = export to Excel. */
  listGenerateChoice: 5 | 10 | 'more' = 5;
  listMoreCount = 50;
  listMoreAll = false;
  isStartingListGeneration = false;
  listExportDialogOpen = false;
  saveIcpOpen = false;
  deleteListConfirmOpen = false;
  isDeletingList = false;
  /** Excel export of this list being tracked in the progress card. */
  activeExport: LeadExport | null = null;
  isCancellingExport = false;
  isDownloadingExport = false;
  /** CSV export / bulk-unlock modal (list mode and ICP mode). */
  csvExportOpen = false;
  creditBalance: number | null = null;
  /** Ticks every 30 s in list mode to refresh the "Expires in" labels. */
  now = Date.now();
  readonly maxExportLeads = MAX_EXPORT_LEADS;
  /** Missing required fields per ICP id, learnt from save / a 422 icp_incomplete. */
  private icpMissingRequired = new Map<number, string[]>();
  private dismissedLoadedForList: string | null = null;
  private exportPollSub: Subscription | null = null;
  private clockSub: Subscription | null = null;
  private reloadIcpsOnListExit = false;

  /** Leads whose avatar failed to load, so the initials stand in from then on. */
  private photoFailures = new Set<number>();

  private destroy$ = new Subject<void>();
  private pollSub: Subscription | null = null;
  private echoChannelName: string | null = null;
  private userId: number | null = null;

  /** Leads streamed over the websocket, keyed by id so they can't duplicate API rows. */
  private liveLeads = new Map<number, WorkspaceLeadRow>();
  private fetchedLeads: WorkspaceLeadRow[] = [];

  constructor(
    private workspaceService: WorkspaceService,
    private leadsService: WorkspaceLeadsService,
    private icpService: IcpService,
    private echoService: EchoService,
    private authService: AuthService,
    private popupService: PopupService,
    private router: Router,
    private route: ActivatedRoute,
    private prospectingService: ProspectingService,
    private store: Store,
  ) {}

  ngOnInit(): void {
    try {
      const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
      if (storedView === 'grid' || storedView === 'list') {
        this.viewMode = storedView;
      }
      this.configCollapsed = localStorage.getItem(CONFIG_HIDDEN_STORAGE_KEY) === '1';
    } catch { /* storage unavailable — keep the defaults */ }

    // List mode is driven ONLY by ?list= — without it the page is the
    // normal ICP leads page. The route is reused on query changes, so
    // react to every change (enter / leave list mode).
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const listId = params.get('list');
        if (listId && listId !== this.listId) {
          this.enterListMode(listId);
        } else if (!listId && this.listId) {
          this.exitListMode();
        }
      });

    this.store.select(selectCredits)
      .pipe(takeUntil(this.destroy$))
      .subscribe(credits => {
        const value = Number(credits);
        this.creditBalance = Number.isFinite(value) ? value : null;
      });

    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        const id = Number(user?.id);
        if (Number.isFinite(id) && id > 0 && id !== this.userId) {
          this.userId = id;
          this.subscribeToLeadEvents();
        }
      });

    this.loadIcps();

    // Workspaces feed the report-destination dialog only.
    this.workspaceService.getAllWorkspaces()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ workspaces }) => {
          this.workspaces = workspaces;
        },
        error: () => {
          // The picker will fall back to the default workspace server-side.
        }
      });
  }

  private loadIcps(): void {
    this.isLoadingIcps = true;

    this.icpService.getIcps({ per_page: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.icps = data ?? [];
          this.isLoadingIcps = false;
          // In list mode the ICP context stays idle until the user leaves it.
          if (!this.listId) {
            this.restoreSelection();
          }
        },
        error: () => {
          this.isLoadingIcps = false;
          this.popupService.notify('Could not load your ICPs. Please refresh.', '', 'error');
        }
      });
  }

  ngOnDestroy(): void {
    this.stopExportPolling();
    this.clockSub?.unsubscribe();
    this.stopPolling();
    this.leaveLeadEvents();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.scheduleFiltersOverflowCheck();
  }

  // ─── ICP selection ─────────────────────────────────────────────────

  get selectedIcp(): ICP | null {
    return this.icps.find(icp => Number(icp.id) === this.selectedIcpId) ?? null;
  }

  onIcpChange(id: string | number): void {
    const icpId = Number(id);

    if (icpId === CREATE_ICP_OPTION) {
      this.router.navigate(['/icp/create']);
      return;
    }

    if (!Number.isFinite(icpId) || icpId === this.selectedIcpId) {
      return;
    }

    const isSwitch = this.selectedIcpId !== null;

    this.selectedIcpId = icpId;
    try {
      localStorage.setItem(STORAGE_KEY, String(icpId));
    } catch { /* storage unavailable — selection just won't persist */ }

    this.settings = null;
    this.leads = [];
    this.fetchedLeads = [];
    this.liveLeads.clear();
    this.dismissedLeads = [];
    this.dismissedLoadedFor = null;
    this.runs = [];
    this.runsLoadedFor = null;
    this.activeTab = 'active';
    this.activeMeta = { page: 1, lastPage: 1, total: 0 };
    this.dismissedMeta = { page: 1, lastPage: 1, total: 0 };
    this.runsMeta = { page: 1, lastPage: 1, total: 0 };
    this.activeRun = null;
    this.stopPolling();
    this.poolEstimate = null;
    this.advancedOpen = false;
    this.advCategoryGroups = [];
    this.advCategoriesLoadedFor = null;
    this.advCategoriesFiltered = true;
    this.advSelectedCategories.clear();
    this.advProducts = [];
    this.advProductInput = '';
    this.appliedBusinessCategories = [];
    this.appliedCompanyProducts = [];
    this.filtersExpanded = false;
    this.filtersOverflowing = false;

    // Changing to another ICP reloads its config fresh from the saved
    // settings: the local snapshot only protects the CURRENT setup from
    // generation/page reloads, not stale state from an earlier visit.
    if (isSwitch) {
      this.clearManualConfig(icpId);
    }

    this.loadSettings();
    this.loadPoolEstimate();
    this.loadLeads();
  }

  private restoreSelection(): void {
    if (!this.icps.length) {
      return;
    }

    let stored: number | null = null;
    try {
      stored = Number(localStorage.getItem(STORAGE_KEY));
    } catch { /* ignore */ }

    const candidate =
      this.icps.find(icp => Number(icp.id) === stored)
      ?? this.icps.find(icp => (icp as any).is_default)
      ?? this.icps[0];

    this.onIcpChange(candidate.id as any);
  }

  // ─── Tabs & pagination ─────────────────────────────────────────────

  get displayedLeads(): WorkspaceLeadRow[] {
    return this.activeTab === 'active' ? this.leads : this.dismissedLeads;
  }

  get isLoadingCurrentTab(): boolean {
    if (this.activeTab === 'active') {
      return this.isLoadingLeads;
    }
    if (this.activeTab === 'dismissed') {
      return this.isLoadingDismissed;
    }
    return this.isLoadingRuns;
  }

  get currentMeta(): PageMeta {
    if (this.activeTab === 'active') {
      return this.activeMeta;
    }
    if (this.activeTab === 'dismissed') {
      return this.dismissedMeta;
    }
    return this.runsMeta;
  }

  selectTab(tab: LeadsTab): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;

    const dismissedStale = this.listId
      ? this.dismissedLoadedForList !== this.listId
      : this.dismissedLoadedFor !== this.selectedIcpId;

    if (tab === 'dismissed' && dismissedStale) {
      this.loadDismissedLeads(1);
    }

    if (tab === 'activity' && this.runsLoadedFor !== this.selectedIcpId) {
      this.loadRuns(1);
    }
  }

  onPerPageChange(perPage: string | number): void {
    const value = Number(perPage);

    if (!this.perPageOptions.includes(value) || value === this.perPage) {
      return;
    }

    this.perPage = value;

    // Reload whatever has been loaded, from page 1.
    this.loadLeads(1);
    if (this.dismissedLoadedFor === this.selectedIcpId) {
      this.loadDismissedLeads(1);
    }
    if (this.runsLoadedFor === this.selectedIcpId) {
      this.loadRuns(1);
    }
  }

  goToPage(page: number): void {
    const meta = this.currentMeta;

    if (page < 1 || page > meta.lastPage || page === meta.page) {
      return;
    }

    if (this.activeTab === 'active') {
      this.loadLeads(page);
    } else if (this.activeTab === 'dismissed') {
      this.loadDismissedLeads(page);
    } else {
      this.loadRuns(page);
    }
  }

  private loadDismissedLeads(page = 1): void {
    const icpId = this.selectedIcpId;
    const listId = this.listId;
    if (!icpId && !listId) {
      return;
    }

    this.isLoadingDismissed = true;

    const request$ = listId
      ? this.prospectingService.getListLeads(listId, this.perPage, true, page)
      : this.leadsService.getLeads(icpId!, this.perPage, true, page);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (icpId !== this.selectedIcpId || listId !== this.listId) {
            return;
          }
          const paginator = response?.data;
          this.dismissedLeads = paginator?.data ?? [];
          this.dismissedMeta = {
            page: paginator?.current_page ?? page,
            lastPage: paginator?.last_page ?? 1,
            total: paginator?.total ?? this.dismissedLeads.length,
          };
          this.dismissedLoadedFor = icpId;
          this.dismissedLoadedForList = listId;
          this.isLoadingDismissed = false;
        },
        error: () => {
          this.isLoadingDismissed = false;
          this.popupService.notify('Could not load dismissed leads.', '', 'error');
        }
      });
  }

  private loadRuns(page = 1): void {
    const icpId = this.selectedIcpId;
    if (!icpId) {
      return;
    }

    this.isLoadingRuns = true;

    this.leadsService.getRuns(icpId, this.perPage, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (icpId !== this.selectedIcpId) {
            return;
          }
          const paginator = response?.data;
          this.runs = paginator?.data ?? [];
          this.runsMeta = {
            page: paginator?.current_page ?? page,
            lastPage: paginator?.last_page ?? 1,
            total: paginator?.total ?? this.runs.length,
          };
          this.runsLoadedFor = icpId;
          this.isLoadingRuns = false;
        },
        error: () => {
          this.isLoadingRuns = false;
          this.popupService.notify('Could not load the generation activity.', '', 'error');
        }
      });
  }

  runTypeLabel(run: LeadRunRow): string {
    if (run.run_type === 'scheduled') {
      return 'Daily automation';
    }
    if (run.run_type === 'onboarding') {
      return 'Onboarding';
    }
    return 'Manual';
  }

  runStatusClass(run: LeadRunRow): string {
    switch (run.status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-600';
      case 'processing':
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  }

  restoreLead(lead: WorkspaceLeadRow): void {
    if (this.restoringLeads.has(lead.id)) {
      return;
    }

    this.restoringLeads.add(lead.id);

    this.leadsService.restoreLead(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.restoringLeads.delete(lead.id);
          this.dismissedLeads = this.dismissedLeads.filter(l => l.id !== lead.id);

          const restored = { ...lead, ...data, is_dismissed: false };
          this.fetchedLeads = [restored, ...this.fetchedLeads.filter(l => l.id !== lead.id)];
          this.mergeLeads();

          this.popupService.notify(`${this.displayName(lead)} restored to your leads.`, '', 'success');
        },
        error: (error) => {
          this.restoringLeads.delete(lead.id);
          const msg = error?.error?.message || 'Could not restore the lead.';
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  startDeleteLead(lead: WorkspaceLeadRow): void {
    if (this.deletingLeads.has(lead.id)) {
      return;
    }

    this.deleteConfirmLead = lead;
  }

  cancelDeleteLead(): void {
    this.deleteConfirmLead = null;
  }

  confirmDeleteLead(): void {
    const lead = this.deleteConfirmLead;
    this.deleteConfirmLead = null;

    if (!lead || this.deletingLeads.has(lead.id)) {
      return;
    }

    this.deletingLeads.add(lead.id);

    this.leadsService.deleteLead(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingLeads.delete(lead.id);
          this.dismissedLeads = this.dismissedLeads.filter(l => l.id !== lead.id);
          this.fetchedLeads = this.fetchedLeads.filter(l => l.id !== lead.id);
          this.popupService.notify(`${this.displayName(lead)} deleted.`, '', 'success');
        },
        error: (error) => {
          this.deletingLeads.delete(lead.id);
          const msg = error?.error?.message || 'Could not delete the lead.';
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  // ─── Data loading ──────────────────────────────────────────────────

  private loadSettings(): void {
    const icpId = this.selectedIcpId;
    if (!icpId) {
      return;
    }

    this.leadsService.getLeadSettings(icpId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          if (icpId !== this.selectedIcpId) {
            return;
          }
          this.settings = data;
          this.generateCount = data.daily_leads_count;
          this.automationEnabled = data.daily_leads_enabled;
          this.automationCount = data.daily_leads_count;
          this.automationTime = data.daily_leads_time ?? '09:00';
          this.automationTimezone = data.daily_leads_timezone
            ?? Intl.DateTimeFormat().resolvedOptions().timeZone
            ?? 'UTC';
          // The page starts from the automation's saved Advanced filters, so
          // chips and manual generation match what scheduled runs use.
          this.appliedBusinessCategories = [...(data.daily_leads_business_categories ?? [])];
          this.appliedCompanyProducts = [...(data.daily_leads_company_products ?? [])];

          // But the config the user last set on this device wins, so a
          // finished generation (which re-fetches settings) or a page
          // revisit doesn't reset the current setup.
          const stored = this.restoreManualConfig(icpId);
          if (stored) {
            if (stored.count && this.generateCounts.includes(stored.count)) {
              this.generateCount = stored.count;
            }
            this.appliedBusinessCategories = stored.categories;
            this.appliedCompanyProducts = stored.products;
          }
        },
        error: () => {
          this.popupService.notify('Could not load lead settings for this ICP.', '', 'error');
        }
      });
  }

  private loadLeads(page?: number): void {
    const icpId = this.selectedIcpId;
    const listId = this.listId;
    if (!icpId && !listId) {
      return;
    }

    const targetPage = page ?? this.activeMeta.page;
    this.isLoadingLeads = true;

    const request$ = listId
      ? this.prospectingService.getListLeads(listId, this.perPage, false, targetPage)
      : this.leadsService.getLeads(icpId!, this.perPage, false, targetPage);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (icpId !== this.selectedIcpId || listId !== this.listId) {
            return;
          }
          const paginator = response?.data;
          this.fetchedLeads = paginator?.data ?? [];
          this.activeMeta = {
            page: paginator?.current_page ?? targetPage,
            lastPage: paginator?.last_page ?? 1,
            total: paginator?.total ?? this.fetchedLeads.length,
          };
          this.isLoadingLeads = false;
          this.mergeLeads();
        },
        error: () => {
          this.isLoadingLeads = false;
          this.popupService.notify('Could not load leads. Please try again.', '', 'error');
        }
      });
  }

  /**
   * Merge API rows with websocket rows (newest first). Live rows are only
   * merged into page 1 — deeper pages show exactly what the API returned.
   */
  private mergeLeads(): void {
    const merged = new Map<number, WorkspaceLeadRow>();

    for (const lead of this.fetchedLeads) {
      if (lead && typeof lead.id === 'number') {
        merged.set(lead.id, lead);
      }
    }

    if (this.activeMeta.page === 1) {
      for (const [id, lead] of this.liveLeads) {
        const existing = merged.get(id);
        const next: WorkspaceLeadRow = { ...existing, ...lead };

        if (existing?.email_status === 'unlocked' && next.email_status !== 'unlocked') {
          next.email_status = existing.email_status;
          next.person_email = existing.person_email;
        }

        merged.set(id, next);
      }
    }

    this.leads = Array.from(merged.values())
      .filter(l => !l.is_dismissed)
      .sort((a, b) => b.id - a.id);
  }

  // ─── Generation ────────────────────────────────────────────────────

  get isGenerating(): boolean {
    return this.activeRun !== null;
  }

  get generateCounts(): number[] {
    // Only the subscription tier sizes are offered — 5 or 10 leads.
    const min = this.settings?.min_leads ?? 5;
    const max = this.settings?.max_leads ?? 10;
    return min === max ? [min] : [min, max];
  }

  manualCostFor(count: number): number | null {
    if (!this.settings) {
      return null;
    }

    const included = this.settings.included_leads ?? 5;
    const perExtra = this.settings.per_extra_lead ?? 0;

    return this.settings.credit_cost_manual + Math.max(0, count - included) * perExtra;
  }

  get currentGenerateCost(): number | null {
    return this.manualCostFor(this.generateCount);
  }

  get generationProgress(): number {
    if (!this.activeRun || this.activeRun.requested <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.activeRun.found / this.activeRun.requested) * 100));
  }

  toggleConfigCollapsed(): void {
    this.configCollapsed = !this.configCollapsed;
    try {
      localStorage.setItem(CONFIG_HIDDEN_STORAGE_KEY, this.configCollapsed ? '1' : '0');
    } catch { /* storage unavailable — state just won't persist */ }
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch { /* storage unavailable — selection just won't persist */ }
  }

  generateLeads(): void {
    const icpId = this.selectedIcpId;

    if (!icpId || this.isGenerating) {
      return;
    }

    // New leads land on the Leads tab — switch there so the user sees them.
    this.activeTab = 'active';

    // Remember this exact setup locally: settings re-load after the run
    // completes, and the stored config keeps it from being reset.
    this.persistManualConfig();

    this.leadsService.generateLeads(icpId, this.generateCount, this.appliedBusinessCategories, this.appliedCompanyProducts)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.onGenerationStarted(data);
          this.popupService.notify(
            `Generating ${data.leads_requested} leads (${data.credit_cost} credits)...`,
            '',
            'info',
          );
        },
        error: (error) => {
          this.popupService.notify(this.generateErrorMessage(error), '', 'error');
          const runId = error?.error?.run_id;
          if (error?.status === 409 && runId) {
            this.onGenerationStarted({ run_id: runId, leads_requested: this.generateCount } as GenerateLeadsResult);
          }
        }
      });
  }

  private onGenerationStarted(result: GenerateLeadsResult): void {
    this.activeRun = { id: result.run_id, requested: result.leads_requested, found: 0 };
    if (this.settings && typeof result.remaining_generations === 'number') {
      this.settings = { ...this.settings, remaining_manual_generations: result.remaining_generations };
    }
    this.runsLoadedFor = null; // the Activity tab has a new row to show
    this.startPolling();
  }

  private generateErrorMessage(error: any): string {
    const status = error?.status;

    if (status === 402) {
      const required = error?.error?.required;
      return required
        ? `Not enough credits — this generation costs ${required} credits.`
        : 'Not enough credits to generate leads.';
    }
    if (status === 429) {
      return 'Daily manual generation limit reached for this ICP (max '
        + (this.settings?.max_manual_per_day ?? 3) + '/day). Try again tomorrow.';
    }
    if (status === 409) {
      return 'A generation is already running for this ICP.';
    }

    return error?.error?.message || error?.error?.error || 'Failed to start lead generation.';
  }

  private startPolling(): void {
    this.stopPolling();

    this.pollSub = interval(GENERATION_POLL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.leadsService.getRun(this.activeRun!.id)),
      )
      .subscribe({
        next: ({ data }) => {
          if (!this.activeRun) {
            return;
          }

          this.activeRun = { ...this.activeRun, found: data.leads_found ?? this.activeRun.found };

          if (data.status === 'completed' || data.status === 'failed' || data.status === 'skipped') {
            const found = data.leads_found ?? 0;
            const failed = data.status === 'failed';

            this.activeRun = null;
            this.stopPolling();
            this.loadLeads(1);
            this.loadSettings();
            this.runsLoadedFor = null;
            this.refreshListDetail();

            if (failed) {
              this.popupService.notify(
                data.error_message || 'Lead generation failed. Any charge was refunded.',
                '',
                'error',
              );
            } else if (found > 0) {
              this.popupService.notify(`${found} new lead${found === 1 ? '' : 's'} generated.`, '', 'success');
            } else {
              this.popupService.notify(
                this.listId
                  ? 'No new leads found for this list — the charge was refunded. Try other job titles or departments, or broaden your search.'
                  : 'No new leads found for this ICP — the charge was refunded. Consider broadening the ICP targeting.',
                '',
                'info',
              );
            }
          }
        },
        error: () => {
          // Leave the spinner; the next tick retries.
        }
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  // ─── Websocket (live leads) ────────────────────────────────────────

  private subscribeToLeadEvents(retriesLeft = 5): void {
    const echo = this.echoService.getEcho();

    if (!this.userId) {
      return;
    }

    if (!echo) {
      if (retriesLeft > 0) {
        setTimeout(() => this.subscribeToLeadEvents(retriesLeft - 1), 2000);
      }
      return;
    }

    this.leaveLeadEvents();
    this.echoChannelName = `workspace-leads.${this.userId}`;

    echo.private(this.echoChannelName)
      .listen('.workspace-lead.generated', (event: any) => {
        // List mode: only this list's leads (they carry icp_id null).
        const matches = this.listId
          ? (event?.prospecting_list_id ?? event?.lead?.prospecting_list_id) === this.listId
          : Number(event?.icp_id) === this.selectedIcpId;
        if (!event || !matches) {
          return;
        }

        if (event.lead && typeof event.lead.id === 'number') {
          this.liveLeads.set(event.lead.id, event.lead as WorkspaceLeadRow);
          this.mergeLeads();
        }

        if (this.activeRun && Number(event.run_id) === this.activeRun.id) {
          this.activeRun = { ...this.activeRun, found: event.total_generated ?? this.activeRun.found };

          if (event.generation_status === 'completed') {
            this.activeRun = null;
            this.stopPolling();
            this.loadLeads(1);
            this.loadSettings();
            this.runsLoadedFor = null;
            this.refreshListDetail();
            this.popupService.notify(
              `${event.total_generated ?? 0} new lead${event.total_generated === 1 ? '' : 's'} generated.`,
              '',
              'success',
            );
          }
        }
      })
      .listen('.lead-export.progress', (event: any) => {
        const exp: LeadExport | undefined = event?.export;
        if (!exp || !this.listId || exp.prospecting_list_id !== this.listId) {
          return;
        }
        this.applyExportUpdate(exp);
      });
  }

  private leaveLeadEvents(): void {
    if (this.echoChannelName) {
      this.echoService.getEcho()?.leave(this.echoChannelName);
      this.echoChannelName = null;
    }
  }

  // ─── Generation configuration (inline panel) ───────────────────────


  private loadPoolEstimate(): void {
    const icpId = this.selectedIcpId;
    if (!icpId) {
      return;
    }

    this.isLoadingPool = true;
    this.poolEstimate = null;

    this.leadsService.getPoolEstimate(icpId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          if (icpId !== this.selectedIcpId) {
            return;
          }
          this.poolEstimate = data;
          this.isLoadingPool = false;
        },
        error: () => {
          this.isLoadingPool = false;
          // The dialog simply shows "unavailable" — never blocks saving.
        }
      });
  }

  // ─── Advanced filters dialog ───────────────────────────────────────

  openAdvanced(): void {
    this.advancedOpen = true;
    this.advProducts = [...this.appliedCompanyProducts];
    this.advProductInput = '';

    const icpId = this.selectedIcpId;
    if (!icpId) {
      return;
    }

    if (this.advCategoriesLoadedFor === icpId) {
      this.seedAdvCategorySelection();
      return;
    }

    this.isLoadingAdvCategories = true;

    this.leadsService.getBusinessCategories(icpId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          if (icpId !== this.selectedIcpId) {
            return;
          }
          this.isLoadingAdvCategories = false;
          this.advCategoryGroups = data?.groups ?? [];
          this.advCategoriesFiltered = data?.filtered_by_icp_industries ?? false;
          this.advCategoriesLoadedFor = icpId;
          this.seedAdvCategorySelection();
        },
        error: () => {
          // Categories are an optional refinement — the dialog still works
          // for products; the category section shows an unavailable note.
          this.isLoadingAdvCategories = false;
          this.advCategoryGroups = [];
          this.advCategoriesFiltered = true;
          this.advCategoriesLoadedFor = icpId;
        }
      });
  }

  /** Applied refinement when present, otherwise everything (= whole industry). */
  private seedAdvCategorySelection(): void {
    this.advSelectedCategories = this.appliedBusinessCategories.length
      ? new Set(this.appliedBusinessCategories)
      : new Set(this.advAllCategoryNames());
  }

  closeAdvanced(): void {
    this.advancedOpen = false;
  }

  applyAdvanced(): void {
    // All (or none) selected = no category refinement — whole industry.
    this.appliedBusinessCategories = this.advAllCategoriesSelected
      ? []
      : Array.from(this.advSelectedCategories);
    this.appliedCompanyProducts = [...this.advProducts];
    this.filtersExpanded = false;
    this.advancedOpen = false;
    this.persistManualConfig();
  }

  toggleAdvCategory(category: string): void {
    if (this.advSelectedCategories.has(category)) {
      this.advSelectedCategories.delete(category);
    } else {
      this.advSelectedCategories.add(category);
    }
  }

  selectAllAdvCategories(): void {
    this.advSelectedCategories = new Set(this.advAllCategoryNames());
  }

  clearAdvCategories(): void {
    this.advSelectedCategories.clear();
  }

  private advAllCategoryNames(): string[] {
    return this.advCategoryGroups.flatMap(group => group.categories);
  }

  get advAllCategoriesSelected(): boolean {
    const all = this.advAllCategoryNames();

    return all.length > 0 && all.every(category => this.advSelectedCategories.has(category));
  }

  addAdvProduct(): void {
    const terms = this.advProductInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0 && !this.advProducts.includes(t));

    this.advProducts = [...this.advProducts, ...terms].slice(0, 20);
    this.advProductInput = '';
  }

  removeAdvProduct(term: string): void {
    this.advProducts = this.advProducts.filter(t => t !== term);
  }

  removeAppliedCategory(category: string): void {
    this.appliedBusinessCategories = this.appliedBusinessCategories.filter(c => c !== category);
    this.persistManualConfig();
  }

  removeAppliedProduct(term: string): void {
    this.appliedCompanyProducts = this.appliedCompanyProducts.filter(t => t !== term);
    this.persistManualConfig();
  }

  toggleFiltersExpanded(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  private scheduleFiltersOverflowCheck(): void {
    if (this.filtersOverflowCheckScheduled) {
      return;
    }

    this.filtersOverflowCheckScheduled = true;
    requestAnimationFrame(() => {
      this.filtersOverflowCheckScheduled = false;
      this.updateFiltersOverflow();
    });
  }

  private updateFiltersOverflow(): void {
    const el = this.filtersChipsRef?.nativeElement;

    if (!el) {
      if (this.filtersOverflowing) {
        this.filtersOverflowing = false;
      }
      return;
    }

    const overflowing = el.scrollHeight > FILTERS_COLLAPSED_MAX_PX + 1;

    if (this.filtersOverflowing !== overflowing) {
      this.filtersOverflowing = overflowing;
    }

    if (!overflowing && this.filtersExpanded) {
      this.filtersExpanded = false;
    }
  }

  /** Save this ICP's manual config (count + Advanced filters) locally. */
  private persistManualConfig(): void {
    const icpId = this.selectedIcpId;
    if (!icpId) {
      return;
    }
    try {
      const all = JSON.parse(localStorage.getItem(MANUAL_CONFIG_STORAGE_KEY) || '{}');
      all[icpId] = {
        count: this.generateCount,
        categories: this.appliedBusinessCategories,
        products: this.appliedCompanyProducts,
      };
      localStorage.setItem(MANUAL_CONFIG_STORAGE_KEY, JSON.stringify(all));
    } catch {
      // Storage unavailable (private mode etc.) — the page still works,
      // the config just won't survive a settings re-load.
    }
  }

  /** Drop the locally saved manual config for an ICP. */
  private clearManualConfig(icpId: number): void {
    try {
      const all = JSON.parse(localStorage.getItem(MANUAL_CONFIG_STORAGE_KEY) || '{}');
      if (all[icpId] !== undefined) {
        delete all[icpId];
        localStorage.setItem(MANUAL_CONFIG_STORAGE_KEY, JSON.stringify(all));
      }
    } catch {
      // ignore — nothing to clear
    }
  }

  /** The locally saved manual config for an ICP, or null if none/invalid. */
  private restoreManualConfig(icpId: number): { count?: number; categories: string[]; products: string[] } | null {
    try {
      const all = JSON.parse(localStorage.getItem(MANUAL_CONFIG_STORAGE_KEY) || '{}');
      const stored = all[icpId];
      if (!stored || typeof stored !== 'object') {
        return null;
      }
      return {
        count: typeof stored.count === 'number' ? stored.count : undefined,
        categories: Array.isArray(stored.categories) ? stored.categories.filter((c: unknown) => typeof c === 'string') : [],
        products: Array.isArray(stored.products) ? stored.products.filter((p: unknown) => typeof p === 'string') : [],
      };
    } catch {
      return null;
    }
  }

  /** Count badge on the Advanced button: one per active filter type. */
  get advancedFilterCount(): number {
    return (this.appliedBusinessCategories.length ? 1 : 0)
      + (this.appliedCompanyProducts.length ? 1 : 0);
  }

  saveAutomation(): void {
    const icpId = this.selectedIcpId;

    if (!icpId || this.isSavingAutomation) {
      return;
    }

    // One button drives the state: when automation is currently ON the
    // button acts as "Stop automation" (turn it off); when OFF it saves the
    // configured schedule and turns it on.
    const enabling = !this.settings?.daily_leads_enabled;

    if (enabling && this.automationMissingRequired.length) {
      this.popupService.notify(
        `Complete this ICP to start automation — missing: ${missingRequiredLabel(this.automationMissingRequired)}.`,
        '',
        'error',
      );
      return;
    }

    if (enabling && !this.automationTime) {
      this.popupService.notify('Choose a time of day for the daily generation.', '', 'error');
      return;
    }

    this.isSavingAutomation = true;

    this.leadsService.updateLeadSettings(icpId, {
      daily_leads_enabled: enabling,
      daily_leads_count: this.automationCount,
      daily_leads_time: this.automationTime || null,
      // Always persist a concrete timezone so the scheduler runs on the
      // user's clock — never fall back to a NULL that the backend reads
      // as UTC.
      daily_leads_timezone: this.automationTimezone
        || Intl.DateTimeFormat().resolvedOptions().timeZone
        || 'UTC',
      // The applied Advanced filters are saved with the automation so
      // scheduled runs use the same refinement. Stopping leaves them stored.
      ...(enabling ? {
        daily_leads_business_categories: this.appliedBusinessCategories,
        daily_leads_company_products: this.appliedCompanyProducts,
      } : {}),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.isSavingAutomation = false;
          this.settings = data;
          this.popupService.notify(
            data.daily_leads_enabled
              ? `Daily leads on: ${data.daily_leads_count} leads at ${data.daily_leads_time} (${data.daily_leads_timezone}).`
              : 'Daily lead automation turned off for this ICP.',
            '',
            'success',
          );
        },
        error: (error) => {
          this.isSavingAutomation = false;
          if (error?.status === 422 && error?.error?.error === 'icp_incomplete') {
            const missing: string[] = Array.isArray(error?.error?.missing_required) ? error.error.missing_required : [];
            this.icpMissingRequired.set(icpId, missing);
            this.popupService.notify(
              `Complete this ICP to start automation${missing.length ? ' — missing: ' + missingRequiredLabel(missing) : ''}.`,
              '',
              'error',
            );
            return;
          }
          const msg = error?.error?.message || 'Could not save the automation settings.';
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  /**
   * Download every lead of the selected ICP as an Excel-compatible CSV.
   * On the Dismissed tab the dismissed leads are included too.
   */
  exportLeads(): void {
    const icpId = this.selectedIcpId;

    if (!icpId || this.isExporting) {
      return;
    }

    this.isExporting = true;

    this.leadsService.exportLeads(icpId, this.activeTab === 'dismissed')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.isExporting = false;

          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `leads-${(this.selectedIcp?.name ?? 'icp').toString().replace(/[^a-z0-9-]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.isExporting = false;
          this.popupService.notify('Could not export the leads. Please try again.', '', 'error');
        }
      });
  }

  // ─── Lead card helpers ─────────────────────────────────────────────

  trackById(_index: number, lead: WorkspaceLeadRow): number {
    return lead.id;
  }

  trackByRunId(_index: number, run: LeadRunRow): number {
    return run.id;
  }

  displayName(lead: WorkspaceLeadRow): string {
    const name = lead.person_name?.trim();
    if (name) {
      return name;
    }
    const composed = [lead.person_first_name, lead.person_last_name]
      .filter(part => !!part?.trim())
      .join(' ');
    return composed || 'Unknown contact';
  }

  photoUrl(lead: WorkspaceLeadRow): string | null {
    if (this.photoFailures.has(lead.id)) {
      return null;
    }

    const url = lead.person_photo_url?.trim()
      || lead.apollo_person_data?.photo_url?.trim();

    return url || null;
  }

  onPhotoError(lead: WorkspaceLeadRow): void {
    this.photoFailures.add(lead.id);
  }

  initials(lead: WorkspaceLeadRow): string {
    const letters = this.displayName(lead)
      .split(/\s+/)
      .filter(part => /[a-z0-9]/i.test(part))
      .slice(0, 2)
      .map(part => part[0])
      .join('');
    return letters ? letters.toUpperCase() : '?';
  }

  score(lead: WorkspaceLeadRow): number | null {
    const raw = lead.icp_score;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
  }

  scoreClass(lead: WorkspaceLeadRow): string {
    const value = this.score(lead);
    if (value === null) {
      return 'bg-gray-100 text-gray-500';
    }
    if (value >= 80) {
      return 'bg-green-100 text-green-700';
    }
    if (value >= 65) {
      return 'bg-amber-100 text-amber-700';
    }
    return 'bg-red-100 text-red-600';
  }

  /**
   * Backfilled leads scored below the calibrated threshold and were included
   * so the run wasn't empty — badge them so the 65+ guarantee stays honest.
   */
  isBelowThreshold(lead: WorkspaceLeadRow): boolean {
    return !!(lead.below_threshold ?? lead.icp_match_breakdown?.below_threshold);
  }

  emailStatus(lead: WorkspaceLeadRow): 'locked' | 'unlocked' | 'not_found' {
    const status = lead.email_status;
    return status === 'unlocked' || status === 'not_found' ? status : 'locked';
  }

  isNew(lead: WorkspaceLeadRow): boolean {
    return (lead.status ?? 'new') === 'new';
  }

  isVisited(lead: WorkspaceLeadRow): boolean {
    return !this.isNew(lead);
  }

  markVisited(lead: WorkspaceLeadRow): void {
    if (!this.isNew(lead)) {
      return;
    }

    this.patchLead(lead.id, { status: 'viewed' });

    this.leadsService.updateStatus(lead.id, 'viewed')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          // Cosmetic only — the next fetch restores the true status.
        }
      });
  }

  toggleExpanded(lead: WorkspaceLeadRow): void {
    if (this.expandedLeads.has(lead.id)) {
      this.expandedLeads.delete(lead.id);
    } else {
      this.expandedLeads.add(lead.id);
      this.markVisited(lead);
    }
  }

  isExpanded(lead: WorkspaceLeadRow): boolean {
    return this.expandedLeads.has(lead.id);
  }

  // ─── Lead actions ──────────────────────────────────────────────────

  /** Opens the reveal dialog: the user picks how many hidden leads to unlock. */
  startRevealCompanyLeads(lead: WorkspaceLeadRow): void {
    if (!lead.hidden_company_leads_count || this.revealingLeads.has(lead.id)) {
      return;
    }

    this.markVisited(lead);
    this.revealPickerLead = lead;
    this.revealCount = lead.hidden_company_leads_count;
  }

  cancelRevealCompanyLeads(): void {
    this.revealPickerLead = null;
  }

  /** 1..hidden count options for the reveal dialog. */
  get revealCountOptions(): number[] {
    const max = this.revealPickerLead?.hidden_company_leads_count ?? 0;
    return Array.from({ length: max }, (_, i) => i + 1);
  }

  /** The credit calculator: chosen count × per-lead price. */
  get revealCost(): number {
    const perLead = this.settings?.company_reveal_cost_per_lead ?? 2;
    return this.revealCount * perLead;
  }

  confirmRevealCompanyLeads(): void {
    const lead = this.revealPickerLead;
    const count = this.revealCount;

    this.revealPickerLead = null;

    if (!lead || !count || this.revealingLeads.has(lead.id)) {
      return;
    }

    this.revealingLeads.add(lead.id);

    this.leadsService.revealCompanyLeads(lead.id, count)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.revealingLeads.delete(lead.id);
          this.patchLead(lead.id, { hidden_company_leads_count: data.remaining_hidden ?? 0 });
          this.fetchedLeads = [...data.revealed_leads, ...this.fetchedLeads];
          this.mergeLeads();
          this.popupService.notify(
            `${data.revealed_leads.length} more lead${data.revealed_leads.length === 1 ? '' : 's'} at ${lead.company_name} revealed (${data.credits_deducted} credits).`,
            '',
            'success',
          );
        },
        error: (error) => {
          this.revealingLeads.delete(lead.id);
          const msg = error?.status === 402
            ? (error?.error?.message || 'Not enough credits to reveal the additional company leads.')
            : (error?.error?.message || 'Could not reveal the additional company leads.');
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  /** Opens the cost-confirmation dialog (unlocking charges credits). */
  startUnlockEmail(lead: WorkspaceLeadRow): void {
    if (this.emailStatus(lead) !== 'locked' || this.unlockingLeads.has(lead.id)) {
      return;
    }

    this.markVisited(lead);
    this.unlockTarget = lead;
  }

  cancelUnlockEmail(): void {
    this.unlockTarget = null;
  }

  confirmUnlockEmail(): void {
    const lead = this.unlockTarget;
    this.unlockTarget = null;

    if (!lead || this.emailStatus(lead) !== 'locked' || this.unlockingLeads.has(lead.id)) {
      return;
    }

    this.unlockingLeads.add(lead.id);

    this.leadsService.unlockEmail(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.unlockingLeads.delete(lead.id);
          this.patchLead(lead.id, { person_email: data.person_email, email_status: data.email_status || 'unlocked' });
          this.popupService.notify(
            data.credits_deducted === 0
              ? 'Email unlocked from the existing report — no credits charged.'
              : 'Email unlocked.',
            '',
            'success',
          );
        },
        error: (error) => {
          this.unlockingLeads.delete(lead.id);

          const newStatus = error?.error?.email_status;
          if (newStatus && newStatus !== lead.email_status) {
            this.patchLead(lead.id, {
              email_status: newStatus,
              person_email: newStatus === 'unlocked' ? lead.person_email : null,
            });
          }

          const msg = error?.error?.message || 'Could not unlock the email.';
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  startGenerateReport(lead: WorkspaceLeadRow, regenerate = false): void {
    if (this.enhancingLeads.has(lead.id) || (lead.is_enhanced && !regenerate)) {
      return;
    }

    this.markVisited(lead);
    this.reportPickerLead = lead;
    this.reportPickerRegenerate = regenerate;
    this.reportWorkspaceId = this.defaultWorkspaceId();
  }

  cancelGenerateReport(): void {
    this.reportPickerLead = null;
    this.reportPickerRegenerate = false;
    this.reportWorkspaceId = null;
  }

  confirmGenerateReport(): void {
    const lead = this.reportPickerLead;
    const workspaceId = this.reportWorkspaceId;
    const regenerate = this.reportPickerRegenerate;

    this.reportPickerLead = null;
    this.reportPickerRegenerate = false;
    this.reportWorkspaceId = null;

    if (!lead || this.enhancingLeads.has(lead.id) || (lead.is_enhanced && !regenerate)) {
      return;
    }

    const leadName = this.displayName(lead);
    const workspaceName = this.workspaces.find(w => +w.id === workspaceId)?.name;
    this.enhancingLeads.add(lead.id);

    this.leadsService.enhanceLead(lead.id, workspaceId ?? undefined, regenerate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          this.enhancingLeads.delete(lead.id);
          this.patchLead(lead.id, { is_enhanced: true, enhanced_report_id: data?.search_id ?? null });

          // Stay on the Leads page — the report generates in the background
          // and "Open report" points at it once ready.
          const where = workspaceName ? ` in ${workspaceName}` : '';
          this.popupService.notify(`Report of ${leadName} is generating${where}.`, '', 'success');
        },
        error: (error) => {
          this.enhancingLeads.delete(lead.id);
          const status = error?.status;
          const msg = status === 402
            ? 'Not enough credits to generate this report.'
            : status === 400
              ? 'This lead has no LinkedIn profile, so a report cannot be generated.'
              : status === 409
                ? 'A report has already been generated for this lead.'
                : (error?.error?.error || error?.error?.message || 'Failed to generate the report.');
          this.popupService.notify(msg, '', 'error');
        }
      });
  }

  private defaultWorkspaceId(): number | null {
    const preferred = this.workspaces.find(w => w.is_default) ?? this.workspaces[0];
    return preferred ? Number(preferred.id) : null;
  }

  /** Hide the company icon for this lead when its logo URL fails to load. */
  onCompanyLogoError(lead: WorkspaceLeadRow): void {
    this.patchLead(lead.id, { company_logo_url: null });
  }

  openReport(lead: WorkspaceLeadRow): void {
    if (lead.enhanced_report_id) {
      window.open(`/report/${lead.enhanced_report_id}`, '_blank');
    } else {
      this.router.navigate(['/reports']);
    }
  }

  dismiss(lead: WorkspaceLeadRow): void {
    if (this.dismissingLeads.has(lead.id)) {
      return;
    }

    this.dismissingLeads.add(lead.id);

    this.leadsService.dismissLead(lead.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dismissingLeads.delete(lead.id);
          this.fetchedLeads = this.fetchedLeads.filter(l => l.id !== lead.id);
          this.liveLeads.delete(lead.id);
          this.leads = this.leads.filter(l => l.id !== lead.id);

          if (this.dismissedLoadedFor === this.selectedIcpId) {
            this.dismissedLeads = [
              { ...lead, is_dismissed: true, status: 'dismissed' },
              ...this.dismissedLeads.filter(l => l.id !== lead.id),
            ];
          }
        },
        error: () => {
          this.dismissingLeads.delete(lead.id);
          this.popupService.notify('Could not dismiss the lead.', '', 'error');
        }
      });
  }

  private patchLead(leadId: number, patch: Partial<WorkspaceLeadRow>): void {
    const apply = (row: WorkspaceLeadRow): WorkspaceLeadRow =>
      row.id === leadId ? { ...row, ...patch } : row;

    this.fetchedLeads = this.fetchedLeads.map(apply);
    this.leads = this.leads.map(apply);
    this.dismissedLeads = this.dismissedLeads.map(apply);
    const live = this.liveLeads.get(leadId);
    if (live) {
      this.liveLeads.set(leadId, { ...live, ...patch });
    }
  }

  // ─── Prospecting list mode ─────────────────────────────────────────

  get listMode(): boolean {
    return this.listId !== null;
  }

  /** Missing required fields blocking automation for the selected (prospecting) ICP. */
  get automationMissingRequired(): string[] {
    const icp = this.selectedIcp;
    if (!icp) {
      return [];
    }
    const learnt = this.icpMissingRequired.get(Number(icp.id));
    if (learnt) {
      return learnt;
    }
    return icp.source === 'advanced_prospecting' ? (icp.missing_required ?? []) : [];
  }

  get automationMissingLabel(): string {
    return missingRequiredLabel(this.automationMissingRequired);
  }

  /** Clears every lead / run list and stops generation polling. */
  private resetLeadState(): void {
    this.leads = [];
    this.fetchedLeads = [];
    this.liveLeads.clear();
    this.dismissedLeads = [];
    this.dismissedLoadedFor = null;
    this.dismissedLoadedForList = null;
    this.runs = [];
    this.runsLoadedFor = null;
    this.activeTab = 'active';
    this.activeMeta = { page: 1, lastPage: 1, total: 0 };
    this.dismissedMeta = { page: 1, lastPage: 1, total: 0 };
    this.runsMeta = { page: 1, lastPage: 1, total: 0 };
    this.activeRun = null;
    this.stopPolling();
    this.isLoadingLeads = false;
    this.isLoadingDismissed = false;
    this.expandedLeads.clear();
  }

  private enterListMode(listId: string): void {
    this.resetLeadState();
    this.stopExportPolling();

    // The ICP context goes idle; leaving list mode restores the stored ICP.
    this.selectedIcpId = null;
    this.settings = null;
    this.poolEstimate = null;
    this.advancedOpen = false;

    this.listId = listId;
    this.listConfig = null;
    this.listDetail = null;
    this.listPreview = null;
    this.activeExport = null;
    this.listGenerateChoice = 5;
    this.listMoreAll = false;
    this.closeListDialogs();

    const stored = ProspectingService.readConfig(listId);
    if (stored) {
      this.listConfig = stored;
      this.isResolvingList = false;
      this.onListReady();
    } else {
      this.isResolvingList = true;
    }

    this.prospectingService.getList(listId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          if (listId !== this.listId) {
            return;
          }
          this.listDetail = detail;

          if (!this.listConfig) {
            if (detail?.config && Array.isArray(detail.config.filters)) {
              this.listConfig = detail.config;
              ProspectingService.storeConfig(listId, detail.config);
              this.isResolvingList = false;
              this.onListReady();
            } else {
              this.listUnavailable();
              return;
            }
          }

          if (detail.saved_icp_id) {
            this.popupService.notify('This prospecting list was saved as an ICP — showing the ICP instead.', '', 'info');
            try {
              localStorage.setItem(STORAGE_KEY, String(detail.saved_icp_id));
            } catch { /* ignore */ }
            ProspectingService.clearConfig(listId);
            this.leaveListMode();
            return;
          }

          this.resumeListActivity(detail);
        },
        error: () => {
          if (listId !== this.listId) {
            return;
          }
          // A list that was never generated only lives in this tab — a 404
          // is expected then. Without a local copy there's nothing to show.
          if (!this.listConfig) {
            this.listUnavailable();
          }
        }
      });
  }

  private onListReady(): void {
    this.loadLeads(1);
    this.loadListPreview();

    this.now = Date.now();
    this.clockSub?.unsubscribe();
    this.clockSub = interval(30000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.now = Date.now());
  }

  private listUnavailable(): void {
    const listId = this.listId;
    this.isResolvingList = false;
    if (listId) {
      ProspectingService.clearConfig(listId);
    }
    this.popupService.notify('This prospecting list is no longer available.', '', 'error');
    this.leaveListMode();
  }

  /** Picks up a generation or export still running for the list (e.g. after a refresh). */
  private resumeListActivity(detail: ProspectingListSummary): void {
    if (detail.active_run_id && !this.activeRun) {
      this.activeRun = { id: detail.active_run_id, requested: 0, found: 0 };
      this.startPolling();
    }

    if (detail.active_export_id && (!this.activeExport || this.activeExport.id !== detail.active_export_id)) {
      this.prospectingService.getLeadExport(detail.active_export_id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (exp) => {
            if (this.listId && exp.prospecting_list_id === this.listId) {
              this.setActiveExport(exp);
            }
          },
          error: () => { /* the progress card just doesn't show */ }
        });
    }
  }

  /** Quietly refresh the list summary (lead counts, companies walked). */
  private refreshListDetail(): void {
    const listId = this.listId;
    if (!listId) {
      return;
    }
    this.prospectingService.getList(listId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          if (listId === this.listId) {
            this.listDetail = detail;
          }
        },
        error: () => { /* keep the previous summary */ }
      });
    this.loadListPreview();
  }

  /** Pool + pricing for this list (companies left for "All"). */
  private loadListPreview(): void {
    const listId = this.listId;
    const config = this.listConfig;
    if (!listId || !config) {
      return;
    }
    this.prospectingService.preview(config.filters, listId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preview) => {
          if (listId === this.listId) {
            this.listPreview = preview;
          }
        },
        error: () => { /* prices fall back to the defaults */ }
      });
  }

  private exitListMode(): void {
    this.stopExportPolling();
    this.clockSub?.unsubscribe();
    this.clockSub = null;
    this.closeListDialogs();
    this.csvExportOpen = false;
    this.listId = null;
    this.listConfig = null;
    this.listDetail = null;
    this.listPreview = null;
    this.activeExport = null;
    this.isResolvingList = false;
    this.resetLeadState();

    if (this.reloadIcpsOnListExit) {
      this.reloadIcpsOnListExit = false;
      this.loadIcps();
    } else if (!this.isLoadingIcps) {
      this.restoreSelection();
    }
  }

  private closeListDialogs(): void {
    this.listExportDialogOpen = false;
    this.saveIcpOpen = false;
    this.deleteListConfirmOpen = false;
  }

  /** "Back to ICP leads" — drops ?list= (the query watcher leaves list mode). */
  leaveListMode(): void {
    this.router.navigate(['/leads'], { replaceUrl: true });
  }

  get listTitle(): string {
    return this.listConfig?.summary_label || this.listDetail?.summary_label || 'Prospecting list';
  }

  get listCompaniesLeft(): number | null {
    const left = this.listPreview?.companies_left;
    if (typeof left === 'number') {
      return left;
    }
    const total = this.listConfig?.total_companies ?? this.listDetail?.total_companies;
    if (!total) {
      return null;
    }
    return Math.max(0, total - (this.listDetail?.companies_walked ?? 0));
  }

  get listLeadCount(): number {
    return Math.max(this.listDetail?.leads_count ?? 0, this.activeMeta.total);
  }

  listPrice(count: number): number {
    const fromApi = this.listPreview?.pricing?.manual?.[String(count)];
    return typeof fromApi === 'number' ? fromApi : (DEFAULT_MANUAL_PRICES[count] ?? 0);
  }

  get listMoreCountValid(): boolean {
    const n = Number(this.listMoreCount);
    return this.listMoreAll || (Number.isInteger(n) && n >= 11 && n <= MAX_EXPORT_LEADS);
  }

  /** "Expires in hh:mm" for a prospecting-list lead. */
  expiresIn(lead: WorkspaceLeadRow): string | null {
    if (!lead.expires_at) {
      return null;
    }
    const ms = Date.parse(lead.expires_at) - this.now;
    if (!Number.isFinite(ms)) {
      return null;
    }
    if (ms <= 0) {
      return 'Expired';
    }
    const hours = Math.floor(ms / 3_600_000);
    const minutes = Math.floor((ms % 3_600_000) / 60_000);
    return `Expires in ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  generateListLeads(): void {
    const listId = this.listId;
    const config = this.listConfig;
    const count = this.listGenerateChoice;

    if (!listId || !config || count === 'more' || this.isGenerating || this.isStartingListGeneration) {
      return;
    }

    this.activeTab = 'active';
    this.isStartingListGeneration = true;

    this.prospectingService.generate(listId, config, count)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isStartingListGeneration = false;
          this.onGenerationStarted({ run_id: data.run_id, leads_requested: data.leads_requested } as GenerateLeadsResult);
          this.popupService.notify(
            `Generating ${data.leads_requested} leads (${data.credits_required} credits)...`,
            '',
            'info',
          );
        },
        error: (error) => {
          this.isStartingListGeneration = false;
          const code = error?.error?.error;

          if (error?.status === 409 && code === 'run_in_progress' && error?.error?.run_id) {
            this.popupService.notify('A generation is already running for this list.', '', 'info');
            this.onGenerationStarted({ run_id: error.error.run_id, leads_requested: count } as GenerateLeadsResult);
            return;
          }
          if (error?.status === 422 && code === 'use_export') {
            this.listGenerateChoice = 'more';
            this.openListExportDialog();
            return;
          }
          this.popupService.notify(this.listGenerateErrorMessage(error), '', 'error');
        }
      });
  }

  private listGenerateErrorMessage(error: any): string {
    const status = error?.status;
    const code = error?.error?.error;

    if (status === 402) {
      const required = error?.error?.required;
      const available = error?.error?.available;
      return required
        ? `Not enough credits — this generation costs ${required} credits${available !== undefined ? ` (you have ${available})` : ''}.`
        : 'Not enough credits to generate leads.';
    }
    if (status === 429) {
      return 'Daily manual generation limit reached for this list. Try again tomorrow, or export more leads to Excel.';
    }
    if (status === 409 && code === 'list_saved') {
      return 'This list was saved as an ICP — generate leads from the ICP instead.';
    }
    if (status === 422) {
      const errors = error?.error?.errors;
      const first = errors ? Object.values(errors).flat()[0] : null;
      if (typeof first === 'string' && first) {
        return first;
      }
    }
    return apiErrorMessage(error, 'Failed to start lead generation.');
  }

  /** Excel columns, shared with the export dialog through localStorage. */
  listExportColumns: LeadExportColumn[] = loadExportColumns();

  onListExportColumnsChange(columns: LeadExportColumn[]): void {
    this.listExportColumns = columns;
    saveExportColumns(columns);
  }

  openListExportDialog(): void {
    if (!this.listConfig || (this.activeExport && isLeadExportActive(this.activeExport))) {
      return;
    }
    this.listExportDialogOpen = true;
  }

  closeListExportDialog(): void {
    this.listExportDialogOpen = false;
    this.listExportColumns = loadExportColumns();
  }

  continueExportOpen = false;

  onExportContinued(next: LeadExport): void {
    this.continueExportOpen = false;
    this.setActiveExport(next);
  }

  onListExportStarted(exp: LeadExport): void {
    this.closeListExportDialog();
    this.setActiveExport(exp);
  }

  // ─── Excel export progress (list mode) ─────────────────────────────

  get isExportActive(): boolean {
    return isLeadExportActive(this.activeExport);
  }

  private setActiveExport(exp: LeadExport): void {
    this.activeExport = exp;
    if (isLeadExportActive(exp)) {
      this.startExportPolling(exp.id);
    } else {
      this.stopExportPolling();
    }
  }

  private applyExportUpdate(exp: LeadExport): void {
    if (this.activeExport && exp.id !== this.activeExport.id && !isLeadExportActive(exp)) {
      return;
    }
    const wasActive = isLeadExportActive(this.activeExport) && this.activeExport?.id === exp.id;
    this.setActiveExport(exp);

    if (wasActive && !isLeadExportActive(exp)) {
      this.onExportFinished(exp);
    }
  }

  private onExportFinished(exp: LeadExport): void {
    this.loadLeads(1);
    this.refreshListDetail();

    if (exp.status === 'completed') {
      this.popupService.notify(`Your Excel export is ready — ${exp.leads_found} leads.`, '', 'success');
    } else if (exp.status === 'completed_partial') {
      this.popupService.notify(this.exportStopText(exp), '', 'warning');
    } else if (exp.status === 'failed') {
      this.popupService.notify(exp.error_message || 'The export failed.', '', 'error');
    }
  }

  private startExportPolling(exportId: number): void {
    if (this.exportPollSub && this.activeExport?.id === exportId) {
      return;
    }
    this.stopExportPolling();
    this.exportPollSub = interval(EXPORT_POLL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.prospectingService.getLeadExport(exportId)),
      )
      .subscribe({
        next: (exp) => this.applyExportUpdate(exp),
        error: () => { /* the next tick retries */ }
      });
  }

  private stopExportPolling(): void {
    this.exportPollSub?.unsubscribe();
    this.exportPollSub = null;
  }

  exportStopText(exp: LeadExport): string {
    if (exp.stop_reason === 'insufficient_credits') {
      return `Stopped at ${exp.leads_found} leads (out of credits)`;
    }
    if (exp.stop_reason === 'list_exhausted') {
      return `Finished at ${exp.leads_found} leads — no more matching people in this search`;
    }
    if (exp.stop_reason === 'cancelled') {
      return `Cancelled at ${exp.leads_found} leads`;
    }
    return `Stopped at ${exp.leads_found} leads`;
  }

  cancelExport(): void {
    const exp = this.activeExport;
    if (!exp || this.isCancellingExport) {
      return;
    }
    this.isCancellingExport = true;

    this.prospectingService.cancelLeadExport(exp.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.isCancellingExport = false;
          this.applyExportUpdate(updated);
          this.popupService.notify('Export cancelled — the leads generated so far are kept.', '', 'info');
        },
        error: (error) => {
          this.isCancellingExport = false;
          if (error?.status === 409) {
            // Already finished — show the final state.
            this.prospectingService.getLeadExport(exp.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({ next: (fresh) => this.applyExportUpdate(fresh), error: () => {} });
            return;
          }
          this.popupService.notify(apiErrorMessage(error, 'Could not cancel the export.'), '', 'error');
        }
      });
  }

  downloadActiveExport(): void {
    const exp = this.activeExport;
    if (!exp || this.isDownloadingExport) {
      return;
    }
    this.isDownloadingExport = true;

    this.prospectingService.downloadLeadExport(exp.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.isDownloadingExport = false;
          saveBlob(blob, exp.file_name || `leads-export-${exp.id}.xlsx`);
        },
        error: (error) => {
          this.isDownloadingExport = false;
          this.popupService.notify(
            error?.status === 409 ? 'The export is still running.' : 'Could not download the export.', '', 'error');
        }
      });
  }

  dismissExportCard(): void {
    if (!this.isExportActive) {
      this.activeExport = null;
    }
  }

  openExportsPage(): void {
    this.router.navigate(['/exports'], { queryParams: { tab: 'leads' } });
  }

  // ─── CSV export / bulk unlock modal ────────────────────────────────

  get csvExportScope(): LeadScope | null {
    if (this.listId) {
      return { prospecting_list_id: this.listId };
    }
    return this.selectedIcpId ? { icp_id: this.selectedIcpId } : null;
  }

  get csvExportLabel(): string {
    return this.listId ? this.listTitle : (this.selectedIcp?.name ?? 'icp').toString();
  }

  openCsvExport(): void {
    if (!this.csvExportScope) {
      return;
    }
    this.csvExportOpen = true;
  }

  // ─── Save as ICP / Delete list ─────────────────────────────────────

  onListSaved(result: SaveListResult): void {
    const listId = this.listId;
    const icpId = Number(result?.icp?.id);
    this.saveIcpOpen = false;

    if (listId) {
      ProspectingService.clearConfig(listId);
    }
    if (Number.isFinite(icpId) && icpId > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, String(icpId));
      } catch { /* ignore */ }
      this.icpMissingRequired.set(icpId, result?.missing_required ?? []);
    }

    const missing = result?.missing_required ?? [];
    this.popupService.notify(
      missing.length
        ? `Saved as ICP "${result?.icp?.name ?? ''}". Some ICP fields couldn't be taken from your search (${missingRequiredLabel(missing)}) — complete the ICP to start automation.`
        : `Saved as ICP "${result?.icp?.name ?? ''}".`,
      '',
      missing.length ? 'warning' : 'success',
    );

    // Leave list mode, reload the ICPs and select the new one.
    this.reloadIcpsOnListExit = true;
    this.leaveListMode();
  }

  closeDeleteListConfirm(): void {
    if (!this.isDeletingList) {
      this.deleteListConfirmOpen = false;
    }
  }

  confirmDeleteList(): void {
    const listId = this.listId;
    if (!listId || this.isDeletingList) {
      return;
    }
    this.isDeletingList = true;

    const done = (deleted: number | null) => {
      this.isDeletingList = false;
      this.deleteListConfirmOpen = false;
      ProspectingService.clearConfig(listId);
      this.popupService.notify(
        deleted ? `Prospecting list deleted with its ${deleted} lead${deleted === 1 ? '' : 's'}.` : 'Prospecting list deleted.',
        '',
        'success',
      );
      this.leaveListMode();
    };

    this.prospectingService.deleteList(listId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => done(data?.deleted_leads ?? null),
        error: (error) => {
          if (error?.status === 404) {
            // Never generated — it only existed in this tab.
            done(null);
            return;
          }
          this.isDeletingList = false;
          this.popupService.notify(
            error?.status === 409
              ? 'Wait for the running generation or export to finish, then delete the list.'
              : apiErrorMessage(error, 'Could not delete the list.'),
            '',
            'error',
          );
        }
      });
  }
}
